import type { FlagUser } from '@ecs-na/access-flag-service';
import { getECSobjPropVal, getQueryStringValue } from '../helpers';
import { getMarketingId } from '../return-user';

/**
 * Reads a context value from the URL query string first, then falls back to the
 * tag-manager data. Users frequently land with pc/cc/ref on the URL before (or
 * instead of) those values appearing in `window.ECS.tms.data`, so the query
 * string is the more authoritative source for them.
 */
function getUrlOrTmsValue(key: string): string {
  return String(getQueryStringValue(key) || getECSobjPropVal(key) || '');
}

/**
 * Resolves the current visitor into a {@link FlagUser} for flag evaluation.
 *
 * Identity precedence for the authoritative `id`:
 *   1. marketing id (synchronous — URL `?mkid=` → `marketingid` cookie → tmsData)
 *   2. `window.ECS.tms.data.ecsstaticid`
 *
 * `FeatureFlagRoot` defers flag init until `ecsstaticid` is populated, so by the
 * time this runs the id is present for the vast majority of visitors. We do NOT
 * fall back to a random/anonymous id: a throwaway UUID would mismatch analytics
 * (the logged subject wouldn't equal the visitor's real ecsStaticId) and
 * mislabel `subjectType`. If the tag manager never populates it (blocked/timed
 * out), `id` is left empty rather than inventing an id.
 *
 * Called once on mount by `FlagProvider`, so it is inherently client-side.
 */
export function resolveFlagUser(): FlagUser {
  const marketingId = getMarketingId();
  const ecsStaticId = String(getECSobjPropVal('ecsstaticid') || '');
  const sessionId = String(getECSobjPropVal('sessionid') || '');

  const isReturning = Boolean(marketingId);
  const id = marketingId || ecsStaticId;

  const subjectAttributes: Record<string, string> = {};
  const pageId = String(getECSobjPropVal('pageid') || '');
  // pc/cc/ref: prefer the URL query param, then fall back to tag-manager data.
  const pc = getUrlOrTmsValue('pc');
  const cc = getUrlOrTmsValue('cc');
  const ref = getUrlOrTmsValue('ref');

  if (pageId) {
    subjectAttributes.pageid = pageId;
  }

  if (pc) {
    subjectAttributes.pc = pc;
  }

  if (cc) {
    subjectAttributes.cc = cc;
  }

  if (ref) {
    subjectAttributes.ref = ref;
  }

  // sessionid travels in subjectAttributes (context, not identity) so it is
  // echoed back on the assignment event; the logger reads it for the sub-entity
  // field and re-emits it as context. It is never a secondary identifier.
  if (sessionId) {
    subjectAttributes.sessionid = sessionId;
  }

  // subjectType labels which identifier `id` actually is, so it must track the
  // real source. When neither a marketing id nor an ecsStaticId is available
  // (e.g. the tag manager timed out), `id` is empty — report an empty
  // subjectType rather than falsely claiming 'ecsStaticId'. It is carried in
  // secondaryIds because FlagUser has no first-class field for it; the service
  // flattens secondaryIds onto the assignment event's subjectAttributes, which
  // is the only channel the logger has to read it back and lift it to the
  // top-level uilog `subjectType`. The logger emits an explicit context-only
  // subjectAttributes, so this never leaks onto the wire.
  const subjectType = marketingId ? 'marketingid' : ecsStaticId ? 'ecsStaticId' : '';
  const secondaryIds: Record<string, string> = {
    subjectType,
  };

  // Returning visitors (subject = marketingid) also carry their ecsStaticId as a
  // secondary identifier so analytics can correlate the two. New visitors carry
  // no secondary ids — ecsStaticId is already the subject, so repeating it would
  // be redundant.
  if (isReturning && ecsStaticId) {
    secondaryIds.ecsStaticId = ecsStaticId;
  }

  return {
    id,
    secondaryIds,
    subjectAttributes,
  };
}
